import { TimestampRange } from '@classpass/protobuf-definitions/lib/web/core_types/timestamp_range';
import {
  GetPartnerOrdersRequest,
  GetPartnerOrdersResponse,
} from '@classpass/protobuf-definitions/lib/web/models/partners/sku_order';
import { addYears, startOfDay, subYears } from 'date-fns';

import URLS from 'constants/urls';
import { transformKeysToCamelCase } from 'helpers/core';
import { reportError } from 'helpers/error-logging';
import { httpClient } from 'helpers/http';
import { adjustDateForTimezone, zonedTimeToUtc } from 'helpers/intl-fns';

import { feLifecycleStatusToProto } from '../shared/SkuEnumHelpers';

import {
  DEFAULT_PARTNER_ORDERS_PAGE_SIZE,
  MAX_REQUESTED_PICKUP_TIME_RANGE_YEARS,
  PARTNER_ORDERS_TAB,
} from './SkuOrders.constants';
import { normalizePartnerOrder } from './SkuOrders.normalizers';

// OrderData requires both range bounds; open-ended ranges (e.g. from_inclusive only) 502 on dev.
// The current limit is 3 years in reservation-service's queryOrders.
// Here, there is a temporary business-rule to cap 2 years forward for Today tab and 2 years back for History tab.
export const buildPickupTimeRangePartial = (tab, venueTimezone) => {
  const now = new Date();
  const todayStartInVenue = startOfDay(adjustDateForTimezone(now, venueTimezone));
  const todayStartUtc = zonedTimeToUtc(todayStartInVenue, venueTimezone);

  if (tab === PARTNER_ORDERS_TAB.HISTORY) {
    const historyStartInVenue = subYears(todayStartInVenue, MAX_REQUESTED_PICKUP_TIME_RANGE_YEARS);
    return {
      from_inclusive: zonedTimeToUtc(historyStartInVenue, venueTimezone),
      to_exclusive: todayStartUtc,
    };
  }

  const todayEndInVenue = addYears(todayStartInVenue, MAX_REQUESTED_PICKUP_TIME_RANGE_YEARS);
  return {
    from_inclusive: todayStartUtc,
    to_exclusive: zonedTimeToUtc(todayEndInVenue, venueTimezone),
  };
};

export const buildPickupTimeRange = (tab, venueTimezone) =>
  TimestampRange.toJSON(buildPickupTimeRangePartial(tab, venueTimezone));

export const buildGetPartnerOrdersRequest = ({ venueId, tab, venueTimezone, cursor, lifecycleStatus }) => {
  const parsedVenueId = Number(venueId);
  const lifecycleStatusProto = feLifecycleStatusToProto(lifecycleStatus);

  return GetPartnerOrdersRequest.toJSON({
    venue_ids: Number.isFinite(parsedVenueId) ? [parsedVenueId] : [],
    requested_pickup_time_range: buildPickupTimeRangePartial(tab, venueTimezone),
    cursor_paging: {
      limit: DEFAULT_PARTNER_ORDERS_PAGE_SIZE,
      ...(cursor ? { cursor } : {}),
    },
    ...(lifecycleStatusProto !== undefined ? { lifecycle_status: lifecycleStatusProto } : {}),
  });
};

export async function fetchPartnerOrders({ venueId, tab, venueTimezone, venueName, cursor, lifecycleStatus }) {
  try {
    const body = buildGetPartnerOrdersRequest({ venueId, tab, venueTimezone, cursor, lifecycleStatus });
    const response = await httpClient.post(URLS.skuOrders.getPartnerOrders, body);
    const responseData = response?.data || {};
    const parsed = transformKeysToCamelCase(GetPartnerOrdersResponse.fromJSON(responseData));

    return {
      orders: (responseData.orders || []).map(order => normalizePartnerOrder(order, { venueName })),
      nextPageCursor: parsed.nextPageCursor || null,
    };
  } catch (error) {
    reportError(error);
    throw error;
  }
}
